import React, { useContext, useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Tooltip
} from '@material-ui/core';
import ComputerIcon from '@material-ui/icons/Computer';
import DnsIcon from '@material-ui/icons/Dns';
import StorageIcon from '@material-ui/icons/Storage';
import AppsIcon from '@material-ui/icons/Apps';
import BackupIcon from '@material-ui/icons/Backup';
import CloudIcon from '@material-ui/icons/Cloud';
import MemoryIcon from '@material-ui/icons/Memory';
import SdStorageIcon from '@material-ui/icons/SdStorage';
import DeveloperBoardIcon from '@material-ui/icons/DeveloperBoard';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import BuildIcon from '@material-ui/icons/Build';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Autocomplete from '@material-ui/lab/Autocomplete';
import axios from 'axios';
import * as XLSX from 'xlsx';

import { ClientContext } from '../contexts/ClientContext';
import { AuthContext } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/';

const ManageAssets = () => {
  const { clients, loadClients } = useContext(ClientContext);
  const { token, isSuperAdmin } = useContext(AuthContext);

  const [selectedClientId, setSelectedClientId] = useState('');
  const [assetList, setAssetList] = useState([]); // full list (all clients)
  const [isLoading, setIsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [groupBy, setGroupBy] = useState('none'); // none | client | asset_type | source
  const [lastRefresh, setLastRefresh] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [form, setForm] = useState({ client_id: '', asset_name: '', asset_type: '', brand: '', operating_system: '', ip_address: '', url: '', location_site: '', location_building: '', location_room: '', purpose: '', notes: '' });

  const clientOptions = (clients || []).map((c) => ({
    id: c.id !== undefined ? c.id : c.ID,
    name: c.name !== undefined ? c.name : c.Name,
  }));

  const loadAssets = async () => {
    setIsLoading(true);
    try {
      // Build query parameters: client_id (filter only)
      const params = new URLSearchParams();
      if (selectedClientId) params.append('client_id', selectedClientId);
      const url = `${API_URL}api/maintenance/assets${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let payload = [];
      if (Array.isArray(res.data)) payload = res.data;
      else if (res.data && Array.isArray(res.data.assets)) payload = res.data.assets;
      else if (res.data && (res.data.id !== undefined || res.data.asset_name !== undefined)) payload = [res.data];
      setAssetList(payload);
    } catch (e) {
      setAssetList([]);
    } finally {
      setIsLoading(false);
    }
  };

  const openCreate = () => {
    setEditAsset(null);
    setForm({ client_id: selectedClientId || '', asset_name: '', asset_type: '', notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (asset) => {
    setEditAsset(asset);
    setForm({
      client_id: asset.client_id,
      asset_name: asset.asset_name || '',
      asset_type: asset.asset_type || '',
      brand: asset.brand || '',
      operating_system: asset.operating_system || '',
      ip_address: asset.ip_address || '',
      url: asset.url || '',
      location_site: asset.location_site || '',
      location_building: asset.location_building || '',
      location_room: asset.location_room || '',
      purpose: asset.purpose || '',
      notes: asset.notes || ''
    });
    setDialogOpen(true);
  };

  const saveAsset = async () => {
    try {
      if (editAsset) {
        await axios.put(`${API_URL}api/maintenance/assets/${editAsset.id}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}api/maintenance/assets`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setDialogOpen(false);
      await loadAssets(selectedClientId);
    } catch (e) {
      setDialogOpen(false);
    }
  };

  const deleteAsset = async (asset) => {
    try {
      await axios.delete(`${API_URL}api/maintenance/assets/${asset.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadAssets(selectedClientId);
    } catch (e) {
      // noop
    }
  };

  const syncFromDO = async () => {
    setSyncing(true);
    setSyncSummary(null);
    try {
      const body = selectedClientId ? { client_id: selectedClientId, include_details: true } : {};
      const res = await axios.post(
        `${API_URL}api/maintenance/assets/sync-digitalocean`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSyncSummary(res.data?.summary || null);
      await loadAssets();
    } catch (e) {
      setSyncSummary({ error: e.response?.data?.error || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { loadClients(); }, []);
  useEffect(() => { loadAssets(); }, []); // initial load
  useEffect(() => { loadAssets(); }, [selectedClientId]); // reload on client filter change

  // Load latest DigitalOcean refresh timestamp
  useEffect(() => {
    const loadLastRefresh = async () => {
      try {
        const res = await axios.get(`${API_URL}api/maintenance/digitalocean/sync-logs?limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const log = res.data?.logs?.[0];
        if (log?.completed_at) setLastRefresh(new Date(log.completed_at));
        else if (log?.started_at) setLastRefresh(new Date(log.started_at));
      } catch (_) {}
    };
    loadLastRefresh();
  }, [API_URL, token]);

  // Derive displayed list based on selected client
  const getClientId = (a) => {
    const id = a.client_id !== undefined ? a.client_id : (a.clientId !== undefined ? a.clientId : (a.clientID !== undefined ? a.clientID : null));
    return id !== null && id !== undefined ? String(id).trim() : '';
  };
  const displayedAssets = selectedClientId
    ? assetList.filter(a => getClientId(a) === String(selectedClientId))
    : assetList;

  // Generic grouping helper
  const groupAssets = (assets) => {
    if (groupBy === 'none') return [{ key: 'All Assets', items: assets }];
    let keyFn;
    if (groupBy === 'client') {
      keyFn = (a) => `${getClientId(a)} | ${a.client_name || a.clientName || a.client_id || getClientId(a)}`;
    } else if (groupBy === 'asset_type') {
      keyFn = (a) => a.asset_type || 'unknown';
    } else if (groupBy === 'source') {
      keyFn = (a) => (a.managed_by === 'digitalocean' ? 'DigitalOcean' : 'Manual');
    } else {
      keyFn = () => 'Other';
    }
    const map = new Map();
    assets.forEach((a) => {
      const k = keyFn(a);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(a);
    });
    return Array.from(map.entries())
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
      .map(([k, items]) => ({ key: k, items }));
  };
  const grouped = groupAssets(displayedAssets);

  const sanitizeDescription = (s) => {
    if (!s) return '';
    try {
      const withoutPrice = String(s).replace(/\$\s*\d[\d,]*(?:\.\d+)?\s*(?:\/\s*(?:mo|month))?/gi, '').replace(/\s{2,}/g, ' ').trim();
      return withoutPrice.replace(/\(\s*\)/g, '').trim();
    } catch (_) { return s; }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5">Manage Assets</Typography>
        <Typography variant="body2" color="textSecondary">
          Link maintenance schedules to specific client assets. Create, edit, or remove assets.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: '1 1 260px', minWidth: 240 }}>
            <Autocomplete
              size="small"
              id="assets-client-autocomplete"
              options={clientOptions}
              getOptionLabel={(opt) => `${opt.id} | ${opt.name}`}
              value={clientOptions.find((o) => String(o.id) === String(selectedClientId)) || null}
              onChange={(e, value) => setSelectedClientId(value ? value.id : '')}
              renderInput={(params) => (
                <TextField {...params} label="Client" variant="outlined" required />
              )}
              fullWidth
            />
          </div>
          <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
            <InputLabel>Group By</InputLabel>
            <Select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              label="Group By"
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="client">Client</MenuItem>
              <MenuItem value="asset_type">Asset Type</MenuItem>
              <MenuItem value="source">Source</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" variant="contained" color="primary" onClick={openCreate} disabled={!selectedClientId}>
            Add Asset
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              try {
                const sanitizeDescription = (s) => {
                  if (!s) return '';
                  try {
                    const withoutPrice = String(s).replace(/\$\s*\d[\d,]*(?:\.\d+)?\s*(?:\/\s*(?:mo|month))?/gi, '').replace(/\s{2,}/g, ' ').trim();
                    return withoutPrice.replace(/\(\s*\)/g, '').trim();
                  } catch (_) { return s; }
                };
                const rows = (displayedAssets || []).map(a => ({
                  ID: a.id,
                  Client: a.client_name || a.client_id,
                  Name: a.asset_name,
                  Type: a.asset_type,
                  ManagedBy: a.managed_by,
                  URL: a.url || '',
                  IP: a.ip_address || '',
                  OS: a.operating_system || '',
                  vCPU: a.vcpus ?? '',
                  MemoryMB: a.memory_mb ?? '',
                  DiskGB: a.disk_gb ?? '',
                  Backups: a.backups_enabled === undefined ? '' : (a.backups_enabled ? 'On' : 'Off'),
                  Description: sanitizeDescription(a.description) || '',
                  Active: a.is_active ? 'Yes' : 'No',
                }));
                const ws = XLSX.utils.json_to_sheet(rows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, selectedClientId ? `Client_${selectedClientId}` : 'AllAssets');
                const fname = selectedClientId ? `assets_client_${selectedClientId}.xlsx` : 'assets_all.xlsx';
                XLSX.writeFile(wb, fname);
              } catch (e) {
                console.error('Export failed', e);
              }
            }}
          >
            Export to Excel
          </Button>
          {isSuperAdmin && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={syncFromDO}
              disabled={syncing}
            >
              {syncing ? 'Syncing…' : (selectedClientId ? 'Sync Client' : 'Sync All')}
            </Button>
          )}
          {lastRefresh && (
            <Typography variant="caption" color="textSecondary">
              DigitalOcean last refresh: {lastRefresh.toLocaleString()}
            </Typography>
          )}
          {/* Deprecated: legacy ingest button removed in favor of unified Refresh & Reconcile on DO page */}
        </div>
      </Grid>

      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            {isLoading ? (
              <Typography variant="overline">Loading assets…</Typography>
            ) : displayedAssets.length === 0 ? (
              <Typography variant="body2">No assets{selectedClientId ? ' for this client' : ''} yet. {selectedClientId ? `(Showing ${displayedAssets.length} of ${assetList.length} total)` : ''}</Typography>
            ) : (
              grouped.map(group => (
                <div key={group.key} style={{ marginBottom: 16 }}>
                  {groupBy !== 'none' && (
                    <Typography variant="subtitle2" style={{ borderBottom: '2px solid #ccc', paddingBottom: 4, marginBottom: 8 }}>
                      {group.key} ({group.items.length})
                    </Typography>
                  )}
                  {group.items.map(asset => (
                    <Paper key={asset.id} variant="outlined" style={{ padding: 12, marginBottom: 8, backgroundColor: asset.managed_by === 'digitalocean' ? '#f7fbff' : '#fff' }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={8}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {/* Type icon + name */}
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {(() => {
                                const t = String(asset.asset_type || '').toLowerCase();
                                if (t === 'linux_server' || t === 'windows_server') return <ComputerIcon style={{ fontSize: 18, color: '#607d8b' }} />;
                                if (t === 'application') return <AppsIcon style={{ fontSize: 18, color: '#7e57c2' }} />;
                                if (t === 'database') return <DnsIcon style={{ fontSize: 18, color: '#00838f' }} />;
                                if (t === 'backup') return <BackupIcon style={{ fontSize: 18, color: '#6d4c41' }} />;
                                if (t === 'wordpress') return <StorageIcon style={{ fontSize: 18, color: '#1565c0' }} />;
                                return <StorageIcon style={{ fontSize: 18, color: '#757575' }} />;
                              })()}
                              <Typography variant="subtitle2" style={{ marginRight: 8 }}>
                                {asset.asset_name} <span style={{ color: '#666' }}>({asset.asset_type})</span>
                              </Typography>
                            </span>
                          </div>
                          {/* Client + Source (DO/Manual) tiny text line */}
                          {(groupBy === 'none' || groupBy !== 'source') && (
                            <div style={{ marginTop: 4 }}>
                              <Typography variant="caption" color="textSecondary" style={{ display: 'block', lineHeight: 1.2 }}>
                                {groupBy === 'none' && (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <PersonOutlineIcon style={{ fontSize: 14 }} /> {asset.client_name || asset.client_id}
                                  </span>
                                )}
                                {groupBy === 'none' && groupBy !== 'source' && (
                                  <span style={{ margin: '0 6px' }}>•</span>
                                )}
                                {groupBy !== 'source' && (
                                  asset.managed_by === 'digitalocean' ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                      <CloudIcon style={{ fontSize: 14 }} /> DigitalOcean
                                    </span>
                                  ) : (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                      <BuildIcon style={{ fontSize: 14 }} /> Manual
                                    </span>
                                  )
                                )}
                              </Typography>
                            </div>
                          )}
                          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {asset.url && (
                              <Typography variant="caption" color="textSecondary">URL: {asset.url}</Typography>
                            )}
                            {asset.ip_address && (
                              <Typography variant="caption" color="textSecondary">• IP: {asset.ip_address}</Typography>
                            )}
                            {asset.operating_system && (
                              <Typography variant="caption" color="textSecondary">• OS: {asset.operating_system}</Typography>
                            )}
                          </div>
                          {(asset.vcpus !== undefined || asset.memory_mb !== undefined || asset.disk_gb !== undefined || asset.backups_enabled !== undefined) && (
                            <div style={{ marginTop: 4 }}>
                              <Typography variant="caption" color="textSecondary" style={{ display: 'block', lineHeight: 1.2 }}>
                                {(() => {
                                  const segments = [];
                                  if (asset.vcpus !== undefined) {
                                    segments.push(
                                      <span key="cpu" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        <DeveloperBoardIcon style={{ fontSize: 14 }} /> {asset.vcpus} CPU
                                      </span>
                                    );
                                  }
                                  if (asset.memory_mb !== undefined) {
                                    segments.push(
                                      <span key="mem" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        <MemoryIcon style={{ fontSize: 14 }} /> {asset.memory_mb} MB
                                      </span>
                                    );
                                  }
                                  if (asset.disk_gb !== undefined) {
                                    segments.push(
                                      <span key="disk" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        <SdStorageIcon style={{ fontSize: 14 }} /> {asset.disk_gb} GB
                                      </span>
                                    );
                                  }
                                  if (asset.backups_enabled !== undefined) {
                                    segments.push(
                                      <span key="bk" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        <BackupIcon style={{ fontSize: 14 }} /> {asset.backups_enabled ? 'Backups On' : 'Backups Off'}
                                      </span>
                                    );
                                  }
                                  return segments.reduce((acc, curr, idx) => acc.concat(idx > 0 ? <span key={`sep-${idx}`} style={{ margin: '0 6px' }}>•</span> : null, curr), []);
                                })()}
                              </Typography>
                            </div>
                          )}
                          {sanitizeDescription(asset.description) && (
                            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                              {sanitizeDescription(asset.description)}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={4} style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: 4 }}>
                            <Tooltip title="Edit">
                              <IconButton aria-label="edit" size="small" onClick={() => openEdit(asset)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {asset.managed_by !== 'digitalocean' && (
                              <Tooltip title="Delete">
                                <IconButton aria-label="delete" size="small" onClick={() => deleteAsset(asset)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </div>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </div>
              ))
            )}
            {isSuperAdmin && syncSummary && (
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                {syncSummary.error
                  ? `Sync error: ${syncSummary.error}`
                  : `Synced: created ${syncSummary.created}, updated ${syncSummary.updated}, deactivated ${syncSummary.deactivated} (processed ${syncSummary.total_processed})`}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editAsset ? 'Edit Asset' : 'Add Asset'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth disabled={editAsset?.managed_by === 'digitalocean'}>
                <InputLabel>Client</InputLabel>
                <Select
                  value={form.client_id}
                  onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  label="Client"
                >
                  {clientOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{`${c.id} | ${c.name}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {editAsset?.managed_by === 'digitalocean' && (
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                  Client is set by DigitalOcean project mapping and cannot be changed here.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Asset Name"
                variant="outlined"
                fullWidth
                value={form.asset_name}
                onChange={(e) => setForm({ ...form, asset_name: e.target.value })}
                InputProps={{ readOnly: editAsset?.managed_by === 'digitalocean' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Asset Type"
                variant="outlined"
                fullWidth
                value={form.asset_type}
                onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
                InputProps={{ readOnly: editAsset?.managed_by === 'digitalocean' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Brand"
                variant="outlined"
                fullWidth
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Operating System"
                variant="outlined"
                fullWidth
                value={form.operating_system}
                onChange={(e) => setForm({ ...form, operating_system: e.target.value })}
                InputProps={{ readOnly: editAsset?.managed_by === 'digitalocean' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="IP Address"
                variant="outlined"
                fullWidth
                value={form.ip_address}
                onChange={(e) => setForm({ ...form, ip_address: e.target.value })}
                InputProps={{ readOnly: editAsset?.managed_by === 'digitalocean' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="URL"
                variant="outlined"
                fullWidth
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                // Allow editing URL even for DigitalOcean-managed assets
                InputProps={{ readOnly: false }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Site/Location"
                variant="outlined"
                fullWidth
                value={form.location_site}
                onChange={(e) => setForm({ ...form, location_site: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Building"
                variant="outlined"
                fullWidth
                value={form.location_building}
                onChange={(e) => setForm({ ...form, location_building: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Room"
                variant="outlined"
                fullWidth
                value={form.location_room}
                onChange={(e) => setForm({ ...form, location_room: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Purpose / Usage"
                variant="outlined"
                fullWidth
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
            {editAsset?.managed_by === 'digitalocean' && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  This asset is synced from DigitalOcean. Name, type, IP, URL, and OS are read-only.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={saveAsset} disabled={!form.client_id || !form.asset_name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ManageAssets;
